// src/cashup/BrandingTab.js  (or src/pages/BrandingTab.js if that's what AdminPanel imports)
import React, { useEffect, useMemo, useRef, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebaseConfig";
import { useOrgId } from "../utils/tenant";
import { logAudit } from '../utils/audit';
import useActor from '../utils/useActor';
import { useToast } from "../ui/ToastProvider";

/* ------------------------------ helpers ------------------------------ */
const DEFAULT_BRAND = {
  primary: "#00E1A7",
  accent:  "#4DD0E1",
  bg:      "#0b0f19",
  surface: "#111a2b",
  text:    "#e8edf7",
};
const defaultText = { x: 0.5, y: 0.9, size: 20, weight: 800, italic: false, color: "#e8edf7",
  shadow: { blur: 0, x: 0, y: 0, color: "rgba(0,0,0,0.6)" } };
const defaultSub  = { ...defaultText, size: 13, weight: 500, y: 0.95 };
const defaultImg  = { x: 0.5, y: 0.5, scale: 1, rotate: 0 };

function hexToRgba(hex, a = 1) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return `rgba(20,184,166,${a})`;
  const r = parseInt(m[1], 16), g = parseInt(m[2], 16), b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${a})`;
}
const clamp01 = (n) => Math.max(0, Math.min(1, n));

/* ------------------------------ tiny UI ------------------------------ */
function ColorField({ label, value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex items-center gap-3">
      <label className="w-32 text-slate-300">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-10 w-16 rounded border border-slate-700"
        style={{ background: value }}
        aria-label={`Pick ${label} color`}
      />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-slate-200"
      />
      {open && (
        <div className="rcp-popover top-12 left-40 z-[99999]">
          <div className="rounded-xl p-3 border border-slate-700 shadow-lg" style={{ background: "#0f172a" }}>
            <HexColorPicker color={value} onChange={onChange} />
            <div className="flex justify-end mt-2">
              <button onClick={() => setOpen(false)} className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-sm">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function Slider({ label, min=0, max=1, step=0.01, value, onChange }) {
  return (
    <div className="mb-3">
      <div className="flex justify-between text-slate-300 text-sm mb-1">
        <span>{label}</span><span className="opacity-70">{value}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(e)=>onChange(Number(e.target.value))} className="w-full" />
    </div>
  );
}

/* ----------------------- embedded designer modal ---------------------- */
function DesignerModal({ orgId, brand, splash, onApply, onClose }) {
  const [colors, setColors] = useState({ bg: brand.bg, accent: brand.accent, text: brand.text });
  const [local, setLocal] = useState(() => ({
    enabled: splash.enabled ?? true,
    mode: splash.mode || "background",
    durationSec: splash.durationSec ?? (splash.durationMs ? Math.round(Number(splash.durationMs)/1000) : 5),
    title: splash.title || "",
    subtitle: splash.subtitle || "",
    imageUrl: splash.imageUrl || "",
    titleStyle: { ...defaultText, ...(splash.titleStyle || {}) },
    subtitleStyle: { ...defaultSub,  ...(splash.subtitleStyle || {}) },
    image: { ...defaultImg, ...(splash.image || {}) },
  }));
  const [file, setFile] = useState(null);
  const canvasRef = useRef(null);
  const dragRef = useRef({ type: null });

  const bgFx = useMemo(() => {
    const accent = colors.accent || "#14b8a6";
    return `radial-gradient(1000px 600px at 15% 20%, ${hexToRgba(accent,0.18)}, transparent 40%),
            radial-gradient(900px 600px at 85% 25%, rgba(59,130,246,0.12), transparent 45%)`;
  }, [colors.accent]);

  const startDrag = (type, e) => {
    dragRef.current = { type };
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("touchmove", onDrag, { passive: false });
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchend", endDrag);
  };
  const onDrag = (e) => {
    e.preventDefault?.();
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = ("touches" in e ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = ("touches" in e ? e.touches[0].clientY : e.clientY) - rect.top;
    const x = clamp01(cx / rect.width), y = clamp01(cy / rect.height);
    const t = dragRef.current.type;
    setLocal((s) => {
      if (t === "image") return { ...s, image: { ...s.image, x, y } };
      if (t === "title") return { ...s, titleStyle: { ...s.titleStyle, x, y } };
      if (t === "subtitle") return { ...s, subtitleStyle: { ...s.subtitleStyle, x, y } };
      return s;
    });
  };
  const endDrag = () => {
    window.removeEventListener("mousemove", onDrag);
    window.removeEventListener("touchmove", onDrag);
    window.removeEventListener("mouseup", endDrag);
    window.removeEventListener("touchend", endDrag);
    dragRef.current.type = null;
  };

  const reset = () => {
    setColors({ bg: "#0b0f19", accent: "#4DD0E1", text: "#e8edf7" });
    setLocal({
      enabled: true, mode: "background", durationSec: 5,
      title: "", subtitle: "", imageUrl: "",
      titleStyle: { ...defaultText }, subtitleStyle: { ...defaultSub }, image: { ...defaultImg },
    });
    setFile(null);
  };

  const apply = async () => {
    let imageUrl = local.imageUrl;
    if (file) {
      const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")) : ".png";
      const path = `orgBranding/${orgId}/splash${ext}`;
      const objectRef = ref(storage, path);
      await uploadBytes(objectRef, file);
      imageUrl = await getDownloadURL(objectRef);
    }
    onApply({
      brandPatch: { bg: colors.bg, accent: colors.accent, text: colors.text },
      splashPatch: {
        ...local,
        imageUrl,
        durationMs: Math.max(0, Math.round((local.durationSec ?? 5) * 1000)),
      },
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 grid place-items-center"
         style={{ background: "rgba(0,0,0,.45)", zIndex: 999999 }}>
      <div className="rounded-2xl border border-slate-700 shadow-xl text-slate-200"
           style={{ width: "min(1120px, 96vw)", background: "#0f172a" }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
          <strong>Splash Preview</strong>
          <div className="flex gap-2">
            <button type="button" className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600" onClick={reset}>Reset</button>
            <button type="button" className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600" onClick={onClose}>Close</button>
            <button type="button" className="px-3 py-1 rounded bg-teal-600 hover:bg-teal-500 font-semibold" onClick={apply}>Apply</button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 p-4" style={{ alignItems: "start" }}>
          {/* Canvas (fixed). Only the right controls column scrolls */}
          <div className="rounded-xl relative overflow-hidden border border-slate-700" style={{ background: colors.bg, height: 420 }}>
            <div style={{ position: "absolute", inset: 0, background: bgFx, filter: "blur(.5px)" }} />
            <div ref={canvasRef} className="absolute inset-0">
              {local.imageUrl && local.mode === "cover" && (
                <img alt="" src={local.imageUrl}
                     style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.9 }} />
              )}
              {local.imageUrl && local.mode !== "cover" && (
                <img
                  alt=""
                  src={local.imageUrl}
                  onMouseDown={(e) => startDrag("image", e)}
                  onTouchStart={(e) => startDrag("image", e)}
                  style={{
                    position: "absolute",
                    transform: `translate(-50%, -50%) rotate(${local.image.rotate}deg) scale(${local.image.scale})`,
                    left: `${local.image.x * 100}%`,
                    top: `${local.image.y * 100}%`,
                    width: "420px",
                    cursor: "grab",
                    boxShadow: "0 10px 40px rgba(0,0,0,.35)",
                    borderRadius: 16,
                  }}
                />
              )}
              {!!local.title && (
                <div
                  onMouseDown={(e) => startDrag("title", e)}
                  onTouchStart={(e) => startDrag("title", e)}
                  style={{
                    position: "absolute",
                    transform: "translate(-50%, -50%)",
                    left: `${local.titleStyle.x * 100}%`,
                    top: `${local.titleStyle.y * 100}%`,
                    fontWeight: local.titleStyle.weight,
                    fontSize: local.titleStyle.size,
                    fontStyle: local.titleStyle.italic ? "italic" : "normal",
                    color: local.titleStyle.color || colors.text,
                    textShadow: local.titleStyle.shadow.blur > 0
                      ? `${local.titleStyle.shadow.x}px ${local.titleStyle.shadow.y}px ${local.titleStyle.shadow.blur}px ${local.titleStyle.shadow.color}`
                      : "none",
                    cursor: "grab",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {local.title}
                </div>
              )}
              {!!local.subtitle && (
                <div
                  onMouseDown={(e) => startDrag("subtitle", e)}
                  onTouchStart={(e) => startDrag("subtitle", e)}
                  style={{
                    position: "absolute",
                    transform: "translate(-50%, -50%)",
                    left: `${local.subtitleStyle.x * 100}%`,
                    top: `${local.subtitleStyle.y * 100}%`,
                    fontWeight: local.subtitleStyle.weight,
                    fontSize: local.subtitleStyle.size,
                    fontStyle: local.subtitleStyle.italic ? "italic" : "normal",
                    color: local.subtitleStyle.color || colors.text,
                    textShadow: local.subtitleStyle.shadow.blur > 0
                      ? `${local.subtitleStyle.shadow.x}px ${local.subtitleStyle.shadow.y}px ${local.subtitleStyle.shadow.blur}px ${local.subtitleStyle.shadow.color}`
                      : "none",
                    opacity: 0.9,
                    cursor: "grab",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {local.subtitle}
                </div>
              )}
            </div>
          </div>

          {/* Controls (scrolls) */}
          <div className="rounded-xl border border-slate-700 p-3"
               style={{ background: "#0b1220", maxHeight: 420, overflow: "auto" }}>
            <div className="text-lg font-semibold mb-2">Background</div>

            <div className="mb-3">
              <div className="text-slate-300 text-sm mb-1">Mode</div>
              <select
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1"
                value={local.mode}
                onChange={(e)=>setLocal((s)=>({ ...s, mode: e.target.value }))}
              >
                <option value="background">Background (image centered)</option>
                <option value="cover">Background (image covers)</option>
              </select>
            </div>

            <ColorField label="BG" value={colors.bg} onChange={(v)=>setColors((c)=>({ ...c, bg:v }))}/>
            <ColorField label="Accent" value={colors.accent} onChange={(v)=>setColors((c)=>({ ...c, accent:v }))}/>
            <ColorField label="Text" value={colors.text} onChange={(v)=>setColors((c)=>({ ...c, text:v }))}/>

            <div className="mb-3">
              <div className="text-slate-300 text-sm mb-1">Duration</div>
              <div className="flex items-center gap-2">
                <input type="number" value={local.durationSec} min={0}
                       onChange={(e)=>setLocal((s)=>({ ...s, durationSec: Math.max(0, Number(e.target.value)) }))}
                       className="bg-slate-800 border border-slate-700 rounded px-2 py-1 w-24"/>
                <span className="opacity-70 text-sm">sec</span>
              </div>
            </div>

            <div className="mb-3">
              <div className="text-slate-300 text-sm mb-1">Image</div>
              <div className="flex items-center gap-2">
                <input type="file" accept="image/*" onChange={(e)=>setFile(e.target.files?.[0] || null)} />
                {local.imageUrl && (
                  <button type="button" className="px-3 py-1 rounded bg-rose-600 hover:bg-rose-500 text-sm"
                          onClick={()=>{ setLocal((s)=>({ ...s, imageUrl: "" })); setFile(null); }}>
                    Remove
                  </button>
                )}
                <div className="text-xs opacity-70 truncate max-w-[220px]">
                  {file ? file.name : (local.imageUrl ? local.imageUrl.split("?")[0].split("/").slice(-1)[0] : "No file chosen")}
                </div>
              </div>
            </div>

            <div className="text-lg font-semibold mb-2">Image transform</div>
            <Slider label="Scale"  min={0.2} max={2} step={0.01}
                    value={local.image.scale}  onChange={(v)=>setLocal((s)=>({ ...s, image:{...s.image, scale:v}}))}/>
            <Slider label="Rotate" min={-30} max={30} step={1}
                    value={local.image.rotate} onChange={(v)=>setLocal((s)=>({ ...s, image:{...s.image, rotate:v}}))}/>
            <Slider label="X"      min={0} max={1}  step={0.001}
                    value={local.image.x}      onChange={(v)=>setLocal((s)=>({ ...s, image:{...s.image, x:v}}))}/>
            <Slider label="Y"      min={0} max={1}  step={0.001}
                    value={local.image.y}      onChange={(v)=>setLocal((s)=>({ ...s, image:{...s.image, y:v}}))}/>

            <div className="text-lg font-semibold mt-4 mb-2">Title</div>
            <input className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 mb-2"
                   placeholder="Title" value={local.title} onChange={(e)=>setLocal((s)=>({ ...s, title: e.target.value }))}/>
            <Slider label="Size" min={10} max={48} step={1}
                    value={local.titleStyle.size} onChange={(v)=>setLocal((s)=>({ ...s, titleStyle:{...s.titleStyle, size:v}}))}/>
            <Slider label="Weight" min={300} max={900} step={50}
                    value={local.titleStyle.weight} onChange={(v)=>setLocal((s)=>({ ...s, titleStyle:{...s.titleStyle, weight:v}}))}/>
            <ColorField label="Color" value={local.titleStyle.color}
                        onChange={(v)=>setLocal((s)=>({ ...s, titleStyle:{...s.titleStyle, color:v}}))}/>
            <div className="flex items-center gap-2 mb-3">
              <input type="checkbox" checked={!!local.titleStyle.italic}
                     onChange={(e)=>setLocal((s)=>({ ...s, titleStyle:{...s.titleStyle, italic:e.target.checked}}))}/>
              <span className="text-sm">Italic</span>
            </div>
            <div className="text-slate-300 text-sm mb-1">Position</div>
            <Slider label="X" min={0} max={1} step={0.001}
                    value={local.titleStyle.x} onChange={(v)=>setLocal((s)=>({ ...s, titleStyle:{...s.titleStyle, x:v}}))}/>
            <Slider label="Y" min={0} max={1} step={0.001}
                    value={local.titleStyle.y} onChange={(v)=>setLocal((s)=>({ ...s, titleStyle:{...s.titleStyle, y:v}}))}/>
            <div className="text-slate-300 text-sm mt-2">Shadow</div>
            <Slider label="Blur" min={0} max={10} step={1}
                    value={local.titleStyle.shadow.blur}
                    onChange={(v)=>setLocal((s)=>({ ...s, titleStyle:{...s.titleStyle, shadow:{...s.titleStyle.shadow, blur:v}}}))}/>
            <Slider label="Offset X" min={-6} max={6} step={1}
                    value={local.titleStyle.shadow.x}
                    onChange={(v)=>setLocal((s)=>({ ...s, titleStyle:{...s.titleStyle, shadow:{...s.titleStyle.shadow, x:v}}}))}/>
            <Slider label="Offset Y" min={-6} max={6} step={1}
                    value={local.titleStyle.shadow.y}
                    onChange={(v)=>setLocal((s)=>({ ...s, titleStyle:{...s.titleStyle, shadow:{...s.titleStyle.shadow, y:v}}}))}/>
            <ColorField label="Shadow color" value={local.titleStyle.shadow.color}
                        onChange={(v)=>setLocal((s)=>({ ...s, titleStyle:{...s.titleStyle, shadow:{...s.titleStyle.shadow, color:v}}}))}/>

            <div className="text-lg font-semibold mt-4 mb-2">Subtitle</div>
            <input className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 mb-2"
                   placeholder="Subtitle" value={local.subtitle} onChange={(e)=>setLocal((s)=>({ ...s, subtitle: e.target.value }))}/>
            <Slider label="Size" min={10} max={36} step={1}
                    value={local.subtitleStyle.size} onChange={(v)=>setLocal((s)=>({ ...s, subtitleStyle:{...s.subtitleStyle, size:v}}))}/>
            <Slider label="Weight" min={300} max={900} step={50}
                    value={local.subtitleStyle.weight} onChange={(v)=>setLocal((s)=>({ ...s, subtitleStyle:{...s.subtitleStyle, weight:v}}))}/>
            <ColorField label="Color" value={local.subtitleStyle.color}
                        onChange={(v)=>setLocal((s)=>({ ...s, subtitleStyle:{...s.subtitleStyle, color:v}}))}/>
            <div className="flex items-center gap-2 mb-3">
              <input type="checkbox" checked={!!local.subtitleStyle.italic}
                     onChange={(e)=>setLocal((s)=>({ ...s, subtitleStyle:{...s.subtitleStyle, italic:e.target.checked}}))}/>
              <span className="text-sm">Italic</span>
            </div>
            <div className="text-slate-300 text-sm mb-1">Position</div>
            <Slider label="X" min={0} max={1} step={0.001}
                    value={local.subtitleStyle.x} onChange={(v)=>setLocal((s)=>({ ...s, subtitleStyle:{...s.subtitleStyle, x:v}}))}/>
            <Slider label="Y" min={0} max={1} step={0.001}
                    value={local.subtitleStyle.y} onChange={(v)=>setLocal((s)=>({ ...s, subtitleStyle:{...s.subtitleStyle, y:v}}))}/>
            <div className="text-slate-300 text-sm mt-2">Shadow</div>
            <Slider label="Blur" min={0} max={10} step={1}
                    value={local.subtitleStyle.shadow.blur}
                    onChange={(v)=>setLocal((s)=>({ ...s, subtitleStyle:{...s.subtitleStyle, shadow:{...s.subtitleStyle.shadow, blur:v}}}))}/>
            <Slider label="Offset X" min={-6} max={6} step={1}
                    value={local.subtitleStyle.shadow.x}
                    onChange={(v)=>setLocal((s)=>({ ...s, subtitleStyle:{...s.subtitleStyle, shadow:{...s.subtitleStyle.shadow, x:v}}}))}/>
            <Slider label="Offset Y" min={-6} max={6} step={1}
                    value={local.subtitleStyle.shadow.y}
                    onChange={(v)=>setLocal((s)=>({ ...s, subtitleStyle:{...s.subtitleStyle, shadow:{...s.subtitleStyle.shadow, y:v}}}))}/>
            <ColorField label="Shadow color" value={local.subtitleStyle.shadow.color}
                        onChange={(v)=>setLocal((s)=>({ ...s, subtitleStyle:{...s.subtitleStyle, shadow:{...s.subtitleStyle.shadow, color:v}}}))}/>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- main tab ------------------------------ */
export default function BrandingTab() {
  const actor = useActor();
  const orgId = useOrgId();
  const toast = useToast();

  const [brand, setBrand]   = useState(DEFAULT_BRAND);
  const [splash, setSplash] = useState({
    enabled: true, mode: "background", durationSec: 5, durationMs: 5000,
    title: "", subtitle: "", imageUrl: "", titleStyle: undefined, subtitleStyle: undefined, image: undefined,
  });

  const [saving, setSaving]       = useState(false);
  const [savedOK, setSavedOK]     = useState(false);
  const [previewOpen, setOpen]    = useState(false);

  useEffect(() => {
    if (!orgId) return;
    (async () => {
      const snap = await getDoc(doc(db, "orgs", orgId, "branding", "global"));
      if (snap.exists()) {
        const data = snap.data();
        setBrand({
          primary: data.primary ?? data.brand?.primary ?? DEFAULT_BRAND.primary,
          accent:  data.accent  ?? data.brand?.accent  ?? DEFAULT_BRAND.accent,
          bg:      data.bg      ?? data.brand?.bg      ?? DEFAULT_BRAND.bg,
          surface: data.surface ?? data.brand?.surface ?? DEFAULT_BRAND.surface,
          text:    data.text    ?? data.brand?.text    ?? DEFAULT_BRAND.text,
        });
        if (data.splash) {
          setSplash((s) => ({
            ...s,
            ...data.splash,
            durationSec: data.splash.durationSec ?? (data.splash.durationMs ? Math.round(Number(data.splash.durationMs)/1000) : 5),
          }));
        }
      }
    })().catch(console.error);
  }, [orgId]);

  useEffect(() => {
    const r = document.documentElement.style;
    r.setProperty("--brand-primary", brand.primary);
    r.setProperty("--brand-accent",  brand.accent);
    r.setProperty("--brand-bg",      brand.bg);
    r.setProperty("--brand-surface", brand.surface);
    r.setProperty("--brand-text",    brand.text);
  }, [brand]);

  const save = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      const payload = {
        primary: brand.primary, accent: brand.accent, bg: brand.bg, surface: brand.surface, text: brand.text,
        splash: {
        enabled: !!splash?.enabled,
        durationSec: Number.isFinite(Number(splash?.durationSec)) ? Number(splash.durationSec) : 5,
        durationMs: Math.max(
          0,
          Math.round((Number.isFinite(Number(splash?.durationSec)) ? Number(splash.durationSec) : 5) * 1000)
        ),

        // strings (never undefined)
        mode: (splash?.mode ?? "background"),
        title: (splash?.title ?? ""),
        subtitle: (splash?.subtitle ?? ""),

        // IMPORTANT: only store a URL string (never store File / image objects)
        imageUrl: (splash?.imageUrl ?? ""),

        // optional style objects (must be plain JSON, no undefined)
        titleStyle: {
          ...(splash?.titleStyle ?? {}),
          color: (splash?.titleStyle?.color ?? ""),
          size: Number.isFinite(Number(splash?.titleStyle?.size)) ? Number(splash.titleStyle.size) : 0,
          weight: Number.isFinite(Number(splash?.titleStyle?.weight)) ? Number(splash.titleStyle.weight) : 0,
          x: Number.isFinite(Number(splash?.titleStyle?.x)) ? Number(splash.titleStyle.x) : 0,
          y: Number.isFinite(Number(splash?.titleStyle?.y)) ? Number(splash.titleStyle.y) : 0,
          italic: !!splash?.titleStyle?.italic,
        },

        subtitleStyle: {
          ...(splash?.subtitleStyle ?? {}),
          color: (splash?.subtitleStyle?.color ?? ""),
          size: Number.isFinite(Number(splash?.subtitleStyle?.size)) ? Number(splash.subtitleStyle.size) : 0,
          weight: Number.isFinite(Number(splash?.subtitleStyle?.weight)) ? Number(splash.subtitleStyle.weight) : 0,
          italic: !!splash?.subtitleStyle?.italic,
        },
      },

        updatedAt: serverTimestamp(),
      };
      await setDoc(doc(db, "orgs", orgId, "branding", "global"), payload, { merge: true });

      // Audit (best-effort)
      try {
        await logAudit(db, actor, {
          action: 'SAVE_BRANDING',
          page: 'Branding',
          entityType: 'branding',
          entityId: 'global',
          entityName: orgId,
          storeId: null,
        });
      } catch (e) {}

      setSavedOK(true); setTimeout(()=>setSavedOK(false), 2000);
      try { toast?.success?.("Branding saved."); } catch {}
    } catch (e) {
      console.error(e);
      try { toast?.error?.("Failed to save branding."); } catch {}
    } finally {
      setSaving(false);
    }
  };

  if (!orgId) return <div className="text-slate-300">No org selected.</div>;
  const previewImg = splash.imageUrl || "";

  return (
    <div className="space-y-8 text-slate-200">
      {/* Colors */}
      <div className="rounded-2xl p-6 shadow" style={{ backgroundColor: "var(--brand-surface)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Colors</h3>
          <button type="button" onClick={() => setBrand(DEFAULT_BRAND)} className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-sm">
            Reset to defaults
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <ColorField label="Primary" value={brand.primary} onChange={(v)=>setBrand((b)=>({ ...b, primary:v }))}/>
          <ColorField label="Accent"  value={brand.accent}  onChange={(v)=>setBrand((b)=>({ ...b, accent:v }))}/>
          <ColorField label="Bg"      value={brand.bg}      onChange={(v)=>setBrand((b)=>({ ...b, bg:v }))}/>
          <ColorField label="Surface" value={brand.surface} onChange={(v)=>setBrand((b)=>({ ...b, surface:v }))}/>
          <ColorField label="Text"    value={brand.text}    onChange={(v)=>setBrand((b)=>({ ...b, text:v }))}/>
        </div>
      </div>

      {/* Splash */}
      <div className="rounded-2xl p-6 shadow" style={{ backgroundColor: "var(--brand-surface)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">Splash Screen</h3>
          <button
            type="button"
            className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-sm"
            onClick={() => { setOpen(true); }}
          >
            Preview / Design
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <label className="w-32 text-slate-300">Enabled</label>
            <input type="checkbox" checked={!!splash.enabled} onChange={(e)=>setSplash((s)=>({ ...s, enabled: e.target.checked }))}/>
          </div>

          <div className="flex items-center gap-3">
            <label className="w-32 text-slate-300">Duration</label>
            <input disabled value={splash.durationSec ?? 5}
                   className="bg-slate-800 border border-slate-700 rounded px-3 py-2 w-28 opacity-70" />
            <span className="opacity-70">sec</span>
          </div>

          <div className="flex items-center gap-3">
            <label className="w-32 text-slate-300">Mode</label>
            <input disabled value={splash.mode || "background"}
                   className="bg-slate-800 border border-slate-700 rounded px-3 py-2 flex-1 opacity-70" />
          </div>

          <div className="flex items-center gap-3">
            <label className="w-32 text-slate-300">Title</label>
            <input disabled value={splash.title || ""}
                   className="bg-slate-800 border border-slate-700 rounded px-3 py-2 flex-1 opacity-70" />
          </div>

          <div className="flex items-center gap-3">
            <label className="w-32 text-slate-300">Subtitle</label>
            <input disabled value={splash.subtitle || ""}
                   className="bg-slate-800 border border-slate-700 rounded px-3 py-2 flex-1 opacity-70" />
          </div>
        </div>

        <div className="mt-6">
          <div className="text-sm opacity-80 mb-2">Image preview</div>
          {previewImg && (
            <img alt="preview" src={previewImg}
                 style={{ height: 160, borderRadius: 10, background: "#0b0f19", padding: 8 }} />
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          {savedOK && <span className="text-emerald-400 text-sm">Branding saved!</span>}
          <button
            type="button"
            disabled={saving}
            onClick={save}
            className="px-5 py-2 rounded-lg text-white font-semibold disabled:opacity-60"
            style={{ backgroundColor: "var(--brand-primary)" }}
          >
            {saving ? "Saving…" : "Save Branding"}
          </button>
        </div>
      </div>

      {previewOpen && (
        <DesignerModal
          orgId={orgId}
          brand={brand}
          splash={splash}
          onApply={({ brandPatch, splashPatch }) => {
            setBrand((b)=>({ ...b, ...brandPatch }));
            setSplash((s)=>({ ...s, ...splashPatch }));
            try { useToast?.success?.("Applied. Click Save Branding."); } catch {}
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
